// Phase RENTAL-REINSTATE — reinstate an ended rental modal.
//
// Shown from ``EndedRentalBanner`` when the current admin has the
// ``Rentals / Reinstate`` permission. Presents a reason picker + three
// billing-choice radio options + optional final-invoice void + optional
// customer notification.
//
// Backend: POST /api/admin/rentals/{id}/reinstate — see phase spec for
// the response shape.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import api, { formatApiError } from "@/lib/api";

const REASON_OPTIONS = [
    { value: "ended_by_mistake", label: "Ended by mistake" },
    { value: "customer_changed_mind", label: "Customer changed mind" },
    { value: "contract_dispute_resolved", label: "Contract dispute resolved" },
    { value: "billing_correction", label: "Billing correction" },
    { value: "other", label: "Other (explain below)" },
];

const BILLING_OPTIONS = [
    { value: "resume_today", label: "Resume from today (skip missed cycles)" },
    { value: "backfill", label: "Resume and backfill missed cycles" },
    { value: "not_resumed", label: "Do not resume — I'll re-enable manually" },
];

function fmtMoney(cents) {
    return (Number(cents || 0) / 100).toLocaleString("en-US", {
        style: "currency", currency: "USD",
    });
}

/**
 * Props:
 *   rental — the ended rental doc (needs id, end_date, equipment_type,
 *            unit_number, customer_id, final_invoice_id, deposit).
 *   open, onOpenChange — Dialog control.
 *   onReinstated — callback fired after a successful reinstate; receives
 *                   the response payload.
 */
export function ReinstateRentalModal({ rental, open, onOpenChange, onReinstated }) {
    const [reasonCode, setReasonCode] = useState("");
    const [reasonText, setReasonText] = useState("");
    const [voidInvoice, setVoidInvoice] = useState(true);
    const [billingChoice, setBillingChoice] = useState("resume_today");
    const [notifyCustomer, setNotifyCustomer] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [conflict, setConflict] = useState(null);

    const dep = rental?.deposit || {};
    const depWarnStatus = ["refunded", "forfeited", "fully_applied", "partially_applied"].includes(dep.status)
        ? dep.status
        : null;

    const finalInvId = rental?.final_invoice_id;
    const canSubmit = (
        reasonCode &&
        (reasonCode !== "other" || reasonText.trim().length > 0) &&
        billingChoice &&
        !busy
    );

    const submit = async () => {
        setBusy(true);
        setError(null);
        setConflict(null);
        try {
            const res = await api.post(`/admin/rentals/${rental.id}/reinstate`, {
                reason_code: reasonCode,
                reason_text: reasonText.trim() || undefined,
                void_final_invoice: voidInvoice,
                recurring_billing_choice: billingChoice,
                notify_customer: notifyCustomer,
            });
            const d = res.data;
            toast.success(`Rental reinstated — status is now ${d.resolved_status.replace("_", " ")}`);
            if (d.backfill_invoice) {
                toast(`Backfill invoice ${d.backfill_invoice.invoice_number} created for ${fmtMoney(d.backfill_invoice.amount_cents)}`);
            }
            if (d.final_invoice_voided) {
                toast(`Final invoice ${d.final_invoice_voided.invoice_number} voided`);
            }
            onOpenChange(false);
            setReasonCode(""); setReasonText("");
            setVoidInvoice(true); setBillingChoice("resume_today");
            setNotifyCustomer(false);
            onReinstated && onReinstated(d);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object") {
                if (detail.code === "equipment_conflict") {
                    setConflict(detail);
                } else if (detail.code === "reinstate_internal_error") {
                    // Layer 1 diagnostic — the backend caught an
                    // unhandled exception and packaged the class + message.
                    // Surface it verbatim so the admin can share the root
                    // cause with engineering in one round-trip.
                    setError(
                        `Server error while reinstating: ${detail.message || "unknown"}.` +
                        (detail.hint ? ` ${detail.hint}` : "")
                    );
                } else {
                    setError(detail.message || formatApiError(err));
                }
            } else {
                setError(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="reinstate-rental-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCcw size={18} className="text-emerald-700" />
                        Reinstate rental?
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Clears the end date and re-opens the rental. Any refunded or applied deposit is NOT restored.
                    </DialogDescription>
                </DialogHeader>

                {/* Summary */}
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-0.5"
                    data-testid="reinstate-rental-summary">
                    <div>
                        <span className="text-slate-500">Ended:</span>{" "}
                        <strong>{String(rental?.end_date || "").slice(0, 10) || "—"}</strong>
                        {rental?.end_reason && <> · <span className="text-slate-500">Reason:</span> {rental.end_reason}</>}
                    </div>
                    <div>
                        <span className="text-slate-500">Equipment:</span>{" "}
                        <strong>{(rental?.equipment_type || "").replace("_", " ")} #{rental?.unit_number || "—"}</strong>
                    </div>
                </div>

                {/* Deposit warning */}
                {depWarnStatus && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                        data-testid="reinstate-rental-deposit-warning">
                        <p className="flex items-center gap-1.5 font-semibold">
                            <AlertTriangle size={13} /> Deposit warning
                        </p>
                        <p className="mt-1">
                            Deposit of <strong>{fmtMoney(dep.amount_cents)}</strong> was{" "}
                            {["refunded", "forfeited"].includes(depWarnStatus)
                                ? "returned to customer"
                                : "applied to unpaid balance"}
                            . Reinstate does NOT restore it. If a new deposit is needed for the continued rental, create a deposit invoice.
                        </p>
                        <Link
                            to={`/admin/rentals/${rental?.id}/invoices/new?type=deposit`}
                            className="mt-1.5 inline-block text-blue-900 hover:underline"
                            data-testid="reinstate-rental-create-deposit-invoice-link"
                        >
                            Create deposit invoice →
                        </Link>
                    </div>
                )}

                {/* Equipment conflict banner (only shown after a 409) */}
                {conflict && (
                    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900"
                        data-testid="reinstate-rental-conflict-banner">
                        <p className="flex items-center gap-1.5 font-semibold">
                            <AlertTriangle size={13} /> Equipment conflict
                        </p>
                        <p className="mt-1">
                            Unit #{conflict.unit_number} is now on rental{" "}
                            <Link
                                to={`/admin/rentals/${conflict.conflicting_rental_id}`}
                                className="text-blue-900 hover:underline"
                                data-testid="reinstate-rental-conflict-link"
                            >
                                {conflict.conflicting_rental_short_id}
                            </Link>{" "}
                            (customer: {conflict.conflicting_customer_name || "unknown"}). Unassign that rental first, or use a different unit.
                        </p>
                    </div>
                )}

                {/* Reason */}
                <div className="space-y-1.5">
                    <Label className="text-xs">Reason (required)</Label>
                    <Select value={reasonCode} onValueChange={setReasonCode}>
                        <SelectTrigger data-testid="reinstate-rental-reason-code">
                            <SelectValue placeholder="Pick a reason…" />
                        </SelectTrigger>
                        <SelectContent>
                            {REASON_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value} data-testid={`reinstate-rental-reason-${o.value}`}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Textarea
                        placeholder={reasonCode === "other" ? "Explain the reason (required)" : "Additional context (optional)"}
                        value={reasonText}
                        onChange={(e) => setReasonText(e.target.value.slice(0, 500))}
                        maxLength={500}
                        className="text-xs"
                        rows={2}
                        data-testid="reinstate-rental-reason-text"
                    />
                </div>

                {/* Final invoice void */}
                {finalInvId && (
                    <div className="flex items-start gap-2">
                        <Checkbox
                            id="reinstate-void-inv"
                            checked={voidInvoice}
                            onCheckedChange={(v) => setVoidInvoice(Boolean(v))}
                            data-testid="reinstate-rental-void-invoice"
                        />
                        <Label htmlFor="reinstate-void-inv" className="text-xs leading-snug">
                            Also void the final invoice{" "}
                            <Link
                                to={`/admin/invoices/${finalInvId}`}
                                className="text-blue-900 hover:underline"
                                data-testid="reinstate-rental-final-invoice-link"
                            >
                                (open)
                            </Link>
                        </Label>
                    </div>
                )}

                {/* Recurring billing choice */}
                <div className="space-y-1.5">
                    <Label className="text-xs">Recurring billing</Label>
                    <Select value={billingChoice} onValueChange={setBillingChoice}>
                        <SelectTrigger data-testid="reinstate-rental-billing-choice">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {BILLING_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value} data-testid={`reinstate-rental-billing-${o.value}`}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Notify customer */}
                <div className="flex items-start gap-2">
                    <Checkbox
                        id="reinstate-notify"
                        checked={notifyCustomer}
                        onCheckedChange={(v) => setNotifyCustomer(Boolean(v))}
                        data-testid="reinstate-rental-notify-customer"
                    />
                    <Label htmlFor="reinstate-notify" className="text-xs leading-snug">
                        Notify customer via email
                    </Label>
                </div>

                {error && (
                    <p className="text-xs text-red-700" data-testid="reinstate-rental-error">{error}</p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                        data-testid="reinstate-rental-cancel">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit}
                        className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
                        data-testid="reinstate-rental-submit">
                        <RefreshCcw size={13} /> Reinstate rental
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
